/** The difference between "one screen is broken" and "the app is gone".
 *
 * There was no boundary anywhere in this app, so any error thrown while
 * rendering unmounted the entire tree and left a blank page — no header, no
 * message, no way back. That is exactly what a learner saw when the profile
 * screen read a field the API had silently stopped sending: a white screen,
 * indistinguishable from a broken connection or a dead site.
 *
 * Deliberately plain and dependency-free: it has to survive whatever just
 * failed, so it uses no context, no i18n and no API. Copy is English-only
 * for the same reason — reaching for the translator here would be reaching
 * for the thing that may have just thrown.
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Changing this resets the boundary — pass the route so navigating away
   *  from a broken screen clears it instead of trapping the learner. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The browser console is the only reporting we have; without this the
    // stack is swallowed and the failure is unreproducible.
    console.error("Unhandled render error:", error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="grid min-h-[60vh] place-items-center px-4 py-10">
        <div className="w-full max-w-md rounded-card border border-line bg-surface p-6 text-center">
          <p aria-hidden className="text-3xl">😕</p>
          <h1 className="mt-2 text-lg font-extrabold text-ink">
            This screen didn&rsquo;t load
          </h1>
          <p className="mt-2 text-sm text-muted">
            Something went wrong on our side. Your work is safe — nothing you
            have done has been lost.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="rounded-control border border-line px-4 py-2 text-sm font-semibold text-ink hover:border-brand"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.assign("/dashboard")}
              className="rounded-control bg-brand px-4 py-2 text-sm font-semibold text-white"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
